import * as React from "react";
import { cn } from "../shared/ui/index.ts";
import { Interactions } from "../core/providers/interactions.provider.tsx";
import { I } from "../shared/icons/icons.tsx";
import { usePatientMedia } from "./patient-media.provider.tsx";
import { formatFileSize, formatFileDate } from "./patient-media-previews.ts";
import type { MediaFile, MediaFileType } from "./patient-media.types.ts";

// Each viewer instance registers itself with a stable random ID
function useViewerId() {
  return React.useRef(`viewer-${Math.random().toString(36).slice(2)}`).current;
}

// ── Labels & extensions ──────────────────────────────────────────────────────
const TYPE_LABEL: Record<MediaFileType, string> = {
  jpeg: "Photo clinique",
  png: "Radiographie",
  stl: "Modèle 3D",
  mp4: "Vidéo",
  pdf: "Document PDF",
};

// ── Photo preview ────────────────────────────────────────────────────────────
function PhotoPreview({ fileId }: { fileId: string }) {
  const h = fileId
    .split("")
    .reduce((a, c) => (a * 31 + c.charCodeAt(0)) >>> 0, 0);
  const isIntraoral = fileId.includes("Intrao") || fileId.includes("Occlusal");
  const isProfile = fileId.includes("Profil");
  const skinTone = ["#f5dcc8", "#f0c8a8", "#e8b888", "#fce8d8"][h % 4]!;
  const eyeColor = ["#3d2b1a", "#2d4a7a", "#3d6b3d"][h % 3]!;
  const bg = ["#f0f4f8", "#e8f0e8", "#f4f0f8"][h % 3]!;
  const cx = isProfile ? 140 : 160;

  return (
    <div
      className="flex h-full items-center justify-center"
      style={{
        background:
          "radial-gradient(ellipse at center, #f1f5f9 0%, #e2e8f0 100%)",
      }}
    >
      <div
        className="relative overflow-hidden rounded-2xl shadow-[0_12px_48px_rgba(0,0,0,0.18)]"
        style={{ width: 280, height: 360, maxHeight: "80%", maxWidth: "90%" }}
      >
        {isIntraoral ? (
          <svg
            viewBox="0 0 280 360"
            xmlns="http://www.w3.org/2000/svg"
            width="280"
            height="360"
            style={{ display: "block" }}
          >
            <rect width="280" height="360" fill="#e8d5c4" />
            <ellipse cx="140" cy="200" rx="100" ry="70" fill="#c8a080" />
            <ellipse cx="140" cy="160" rx="92" ry="38" fill="#f0e8d0" />
            {[...Array(7)].map((_, i) => {
              const x = 44 + i * 28;
              const tw = 22 - Math.abs(i - 3) * 2.5;
              const th = 22 + (i === 0 || i === 6 ? 0 : 8);
              return (
                <g key={i}>
                  <rect
                    x={x}
                    y={138}
                    width={tw}
                    height={th}
                    rx={5}
                    fill="white"
                    stroke="#d0c8a0"
                    strokeWidth={1}
                  />
                  <rect
                    x={x}
                    y={175}
                    width={tw}
                    height={th - 2}
                    rx={5}
                    fill="white"
                    stroke="#d0c8a0"
                    strokeWidth={1}
                  />
                </g>
              );
            })}
            <rect
              x={0}
              y={300}
              width={280}
              height={60}
              fill="rgba(0,0,0,0.35)"
            />
            <text
              x={140}
              y={335}
              fontSize={14}
              fill="white"
              textAnchor="middle"
              fontFamily="sans-serif"
            >
              Vue occlusale
            </text>
          </svg>
        ) : (
          <svg
            viewBox="0 0 280 360"
            xmlns="http://www.w3.org/2000/svg"
            width="280"
            height="360"
            style={{ display: "block" }}
          >
            <rect width="280" height="360" fill={bg} />
            <ellipse
              cx={cx}
              cy={130}
              rx={isProfile ? 50 : 65}
              ry={75}
              fill={skinTone}
            />
            <ellipse
              cx={cx}
              cy={270}
              rx={isProfile ? 75 : 95}
              ry={80}
              fill={skinTone}
            />
            {isProfile ? (
              <>
                <ellipse cx={175} cy={115} rx={16} ry={18} fill="white" />
                <circle cx={175} cy={117} r={10} fill={eyeColor} />
                <circle cx={177} cy={113} r={3} fill="white" />
                <path
                  d="M152 148 Q168 158 184 148"
                  stroke="#b06040"
                  strokeWidth={2.5}
                  fill="none"
                  strokeLinecap="round"
                />
                <ellipse
                  cx={148}
                  cy={105}
                  rx={10}
                  ry={4}
                  fill={skinTone}
                  stroke="#a08060"
                  strokeWidth={1}
                />
              </>
            ) : (
              <>
                <ellipse cx={133} cy={112} rx={16} ry={18} fill="white" />
                <ellipse cx={187} cy={112} rx={16} ry={18} fill="white" />
                <circle cx={133} cy={114} r={10} fill={eyeColor} />
                <circle cx={187} cy={114} r={10} fill={eyeColor} />
                <circle cx={136} cy={110} r={3.5} fill="white" />
                <circle cx={190} cy={110} r={3.5} fill="white" />
                <path
                  d="M138 148 Q160 162 182 148"
                  stroke="#b06040"
                  strokeWidth={2.5}
                  fill="none"
                  strokeLinecap="round"
                />
                <rect
                  x={140}
                  y={160}
                  width={62}
                  height={14}
                  rx={7}
                  fill="white"
                  opacity={0.7}
                />
              </>
            )}
            <rect
              x={0}
              y={300}
              width={280}
              height={60}
              fill="rgba(0,0,0,0.35)"
            />
            <text
              x={140}
              y={335}
              fontSize={14}
              fill="white"
              textAnchor="middle"
              fontFamily="sans-serif"
            >
              {isProfile ? "Vue de profil" : "Vue de face"}
            </text>
          </svg>
        )}
      </div>
    </div>
  );
}

// ── X-ray preview ─────────────────────────────────────────────────────────────
function XrayPreview({ fileId }: { fileId: string }) {
  const isPanoramic = fileId.includes("pan") || fileId.includes("Pan");
  const numTeeth = isPanoramic ? 14 : 8;
  const spacing = isPanoramic ? 22 : 28;
  const startX = isPanoramic ? 8 : 14;

  return (
    <div
      className="flex h-full items-center justify-center"
      style={{ background: "#06090f" }}
    >
      <div
        className="overflow-hidden rounded-xl shadow-[0_0_60px_rgba(0,100,200,0.2)]"
        style={{ width: isPanoramic ? 480 : 320, maxWidth: "95%" }}
      >
        <svg
          viewBox={`0 0 ${isPanoramic ? 480 : 320} 220`}
          xmlns="http://www.w3.org/2000/svg"
          width="100%"
          style={{ display: "block" }}
        >
          <rect width="100%" height="220" fill="#06090f" />
          <g opacity="0.8">
            {[...Array(numTeeth)].map((_, i) => {
              const x = startX + i * spacing + (i % 3);
              const toothH = 38 + (i % 4) * 12;
              return (
                <g key={i}>
                  <rect
                    x={x}
                    y={90 - toothH / 2}
                    width={spacing - 3}
                    height={toothH}
                    rx={8}
                    fill="#7ab8e8"
                    opacity={0.65}
                  />
                  <ellipse
                    cx={x + (spacing - 3) / 2}
                    cy={90 - toothH / 2 + 7}
                    rx={(spacing - 5) / 2}
                    ry={5}
                    fill="#a0d0f8"
                    opacity={0.55}
                  />
                  <ellipse
                    cx={x + (spacing - 3) / 2}
                    cy={90 + toothH / 2 - 7}
                    rx={(spacing - 5) / 2}
                    ry={5}
                    fill="#a0d0f8"
                    opacity={0.55}
                  />
                  <rect
                    x={x + 3}
                    y={82}
                    width={spacing - 9}
                    height={16}
                    rx={4}
                    fill="#3a6a9a"
                    opacity={0.4}
                  />
                </g>
              );
            })}
          </g>
          <rect x={0} y={175} width="100%" height={45} fill="#040810" />
          <text
            x="50%"
            y={202}
            fontSize={11}
            fill="#4a8ab0"
            textAnchor="middle"
            fontFamily="monospace"
          >
            {isPanoramic
              ? "PANORAMIQUE DENTAIRE · 2024-03-15"
              : "TÉLÉRADIOGRAPHIE · 2024-03-15"}
          </text>
          <rect
            x={isPanoramic ? 440 : 280}
            y={8}
            width={38}
            height={18}
            rx={3}
            fill="#0a2040"
            stroke="#1a4060"
            strokeWidth={0.8}
          />
          <text
            x={isPanoramic ? 459 : 299}
            y={20}
            fontSize={9}
            fill="#4a8ab0"
            textAnchor="middle"
            fontFamily="monospace"
          >
            DR M.
          </text>
        </svg>
      </div>
    </div>
  );
}

// ── Three.js STL preview ─────────────────────────────────────────────────────
function StlPreview({ fileId }: { fileId: string }) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const isMaxi =
    fileId.includes("maxi") ||
    fileId.includes("Maxi") ||
    fileId.includes("max");
  const isSuperposition = fileId.includes("Super") || fileId.includes("super");

  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let animId: number;
    let cleanup: (() => void) | null = null;

    import("three").then((THREE) => {
      if (!container) return;
      const w = container.clientWidth || 400;
      const h = container.clientHeight || 300;

      const renderer = new THREE.WebGLRenderer({
        antialias: true,
        alpha: false,
      });
      renderer.setSize(w, h);
      renderer.setPixelRatio(window.devicePixelRatio);
      renderer.setClearColor(0x13103a, 1);
      container.appendChild(renderer.domElement);

      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(42, w / h, 0.1, 100);
      camera.position.set(0, 2.2, 5.5);
      camera.lookAt(0, 0.3, 0);

      // Lights
      scene.add(new THREE.AmbientLight(0xffffff, 0.45));
      const d1 = new THREE.DirectionalLight(0xffffff, 0.9);
      d1.position.set(4, 6, 4);
      scene.add(d1);
      const d2 = new THREE.DirectionalLight(0x8080ff, 0.35);
      d2.position.set(-4, 2, -3);
      scene.add(d2);

      const group = new THREE.Group();

      // Gum base
      const gumGeo = new THREE.CylinderGeometry(
        2.1,
        2.1,
        0.28,
        32,
        1,
        false,
        Math.PI * 0.05,
        Math.PI * 0.9,
      );
      const gumMat = new THREE.MeshPhongMaterial({
        color: 0xd06070,
        shininess: 35,
      });
      const gumMesh = new THREE.Mesh(gumGeo, gumMat);
      gumMesh.position.y = -0.14;
      group.add(gumMesh);

      // Teeth along arch
      const numTeeth = isMaxi ? 14 : 12;
      const toothMat = new THREE.MeshPhongMaterial({
        color: 0xf2ede0,
        shininess: 90,
        specular: 0x888888,
      });
      const hashVal = fileId
        .split("")
        .reduce((a, c) => (a * 31 + c.charCodeAt(0)) >>> 0, 0);

      for (let i = 0; i < numTeeth; i++) {
        const t = (i / (numTeeth - 1)) * Math.PI;
        const cx = -Math.cos(t - Math.PI / 2) * 1.85;
        const cz = Math.sin(Math.abs(t - Math.PI / 2)) * 0.65 - 0.2;
        const toothH = 0.42 + ((hashVal + i * 7) % 5) * 0.07;
        const toothW =
          i < 2 || i > numTeeth - 3
            ? 0.16
            : i < 4 || i > numTeeth - 5
              ? 0.22
              : 0.27;

        const toothGeo = new THREE.CylinderGeometry(
          toothW / 2,
          (toothW / 2) * 0.9,
          toothH,
          8,
        );
        const tooth = new THREE.Mesh(toothGeo, toothMat);
        tooth.position.set(cx, toothH / 2 + 0.06, cz);
        tooth.rotation.y = -(t - Math.PI / 2) * 0.4;
        group.add(tooth);
      }

      // Superposition overlay
      if (isSuperposition) {
        const overlayMat = new THREE.MeshPhongMaterial({
          color: 0x22d3ee,
          transparent: true,
          opacity: 0.35,
          wireframe: false,
        });
        const overlayGeo = new THREE.CylinderGeometry(
          2.0,
          2.0,
          0.15,
          32,
          1,
          false,
          Math.PI * 0.05,
          Math.PI * 0.9,
        );
        const overlay = new THREE.Mesh(overlayGeo, overlayMat);
        overlay.position.y = 0.2;
        group.add(overlay);
      }

      scene.add(group);

      // Slight tilt
      group.rotation.x = -0.18;

      let angle = 0;
      const animate = () => {
        animId = requestAnimationFrame(animate);
        angle += 0.007;
        group.rotation.y = angle;
        renderer.render(scene, camera);
      };
      animate();

      cleanup = () => {
        cancelAnimationFrame(animId);
        renderer.dispose();
        if (container.contains(renderer.domElement)) {
          container.removeChild(renderer.domElement);
        }
      };
    });

    return () => {
      cleanup?.();
    };
  }, [fileId, isMaxi, isSuperposition]);

  return (
    <div
      className="flex h-full flex-col overflow-hidden"
      style={{ background: "#13103a" }}
    >
      <div ref={containerRef} className="min-h-0 flex-1" />
      <div className="flex shrink-0 items-center justify-center gap-4 py-2">
        {["Vertex", "Faces", "Maillage"].map((l, i) => (
          <div key={l} className="flex flex-col items-center">
            <span className="text-[12px] font-bold text-violet-300">
              {[24680, 49352, 24676][i]!.toLocaleString()}
            </span>
            <span className="text-[10px] text-violet-500/70">{l}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Video preview ─────────────────────────────────────────────────────────────
function VideoPreview() {
  const [playing, setPlaying] = React.useState(false);
  return (
    <div
      className="flex h-full items-center justify-center"
      style={{ background: "#0a0f1a" }}
    >
      <div
        className="relative overflow-hidden rounded-2xl shadow-[0_12px_48px_rgba(0,0,0,0.5)]"
        style={{ width: 380, height: 240, maxWidth: "95%" }}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            background: "linear-gradient(135deg, #0f2942 0%, #1a3a5c 100%)",
          }}
        />
        {!playing ? (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            className="absolute inset-0 flex items-center justify-center"
          >
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm transition-all hover:bg-white/30 hover:scale-105">
              <I.Play size={34} className="ml-1.5 text-white" />
            </div>
          </button>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
            <div className="h-2.5 w-2.5 animate-bounce rounded-full bg-cyan-400" />
            <span className="text-[13px] font-semibold text-cyan-300">
              Lecture en cours…
            </span>
            <span className="text-[11px] text-cyan-400/60">(démo)</span>
          </div>
        )}
        <div className="absolute bottom-0 left-0 right-0 bg-linear-to-t from-black/80 px-4 py-3">
          <div className="mb-1.5 h-1 w-full overflow-hidden rounded-full bg-white/20">
            <div
              className={cn(
                "h-full rounded-full bg-cyan-400 transition-all duration-500",
                playing ? "w-1/3" : "w-0",
              )}
            />
          </div>
          <div className="flex items-center justify-between text-[10px] text-white/60">
            <span>{playing ? "0:24" : "0:00"}</span>
            <span className="font-semibold text-white">
              Évolution traitement — 6 mois
            </span>
            <span>1:38</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── PDF preview ───────────────────────────────────────────────────────────────
function PdfPreview({ name }: { name: string }) {
  return (
    <div
      className="flex h-full items-center justify-center"
      style={{ background: "#f8fafc" }}
    >
      <div
        className="overflow-hidden rounded-xl border border-sf-border shadow-[0_12px_40px_rgba(0,0,0,0.12)]"
        style={{ width: 260, height: 340 }}
      >
        <div className="flex h-14 items-center justify-between bg-red-600 px-5">
          <span className="text-[15px] font-bold text-white">PDF</span>
          <I.FileText size={20} className="text-white/70" />
        </div>
        <div className="bg-white px-5 py-5">
          <div className="mb-4 text-[13px] font-bold leading-snug text-slate-800">
            {name.replace(/-/g, " ")}
          </div>
          {[0.9, 0.7, 0.85, 0.6, 0.78, 0.45, 0.68, 0.52, 0.8].map((w, i) => (
            <div
              key={i}
              className="mb-2 h-2 rounded-full bg-slate-200"
              style={{ width: `${w * 100}%` }}
            />
          ))}
          <div className="mt-5 h-14 w-full rounded border border-slate-200 bg-slate-50" />
          <div className="mt-4">
            {[0.8, 0.6, 0.72].map((w, i) => (
              <div
                key={i}
                className="mb-2 h-2 rounded-full bg-slate-200"
                style={{ width: `${w * 100}%` }}
              />
            ))}
          </div>
        </div>
        <div className="border-t border-sf-border bg-sf-surface-alt px-5 py-2 text-[10px] text-sf-text-muted">
          Cabinet Ligam · 2024
        </div>
      </div>
    </div>
  );
}

// ── Empty state ───────────────────────────────────────────────────────────────
function EmptyState({ msg }: { msg: string }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 py-8 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-sf-primary-50 text-sf-primary">
        <I.Image size={26} />
      </div>
      <p className="text-[12.5px] text-sf-text-muted">{msg}</p>
    </div>
  );
}

// ── Widget ────────────────────────────────────────────────────────────────────
export function MediaViewerWidget() {
  const viewerId = useViewerId();
  const { current } = Interactions.usePatient();
  const { registerViewer, unregisterViewer, getViewerFile } = usePatientMedia();

  React.useEffect(() => {
    registerViewer(viewerId);
    return () => unregisterViewer(viewerId);
  }, [viewerId, registerViewer, unregisterViewer]);

  const file = getViewerFile(viewerId);

  if (!current) return <EmptyState msg="Sélectionnez un patient" />;
  if (!file)
    return (
      <EmptyState msg="Cliquez sur un fichier dans « Fichiers du patient »" />
    );

  const f = file as MediaFile;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Header */}
      <div className="flex shrink-0 items-center gap-2 border-b border-sf-border bg-white px-3 py-2">
        <div className="min-w-0 flex-1">
          <div className="truncate text-[12.5px] font-semibold text-sf-text">
            {f.name}
          </div>
          <div className="text-[10.5px] text-sf-text-muted">
            {TYPE_LABEL[f.type]}
            {f.date ? ` · ${formatFileDate(f.date)}` : ""}
            {f.size ? ` · ${formatFileSize(f.size)}` : ""}
          </div>
        </div>
        <button
          type="button"
          className="inline-flex h-7 w-7 items-center justify-center rounded border border-sf-border bg-white text-sf-text-muted hover:bg-gray-50"
          title="Télécharger"
        >
          <I.Download size={13} />
        </button>
      </div>

      {/* Preview */}
      <div className="min-h-0 flex-1 overflow-hidden">
        {f.type === "jpeg" && <PhotoPreview fileId={f.id} />}
        {f.type === "png" && <XrayPreview fileId={f.id} />}
        {f.type === "stl" && <StlPreview fileId={f.id} />}
        {f.type === "mp4" && <VideoPreview />}
        {f.type === "pdf" && <PdfPreview name={f.name} />}
      </div>
    </div>
  );
}
